import React, { useEffect, useReducer, useRef, useState } from "react";
import {
    Modal,
    Spin,
    Button,
    Input,
    InputNumber,
    Tag,
    Empty,
    Typography,
    Divider,
    Alert,
    message,
    Table,
    Grid
} from "antd";
import { t } from "i18next";
import { v4 } from "uuid";
import {
    PlusOutlined,
    DeleteOutlined,
    SaveOutlined,
    ClearOutlined,
    SearchOutlined,
    SmallDashOutlined,
    ApartmentOutlined
} from "@ant-design/icons";
import ApiService from "../../core/service/ApiService";
import Helper from "../../core/Helper";
import { Currency } from "../Payment/MultiplePaymentApp";
import { PriceQuoteStorage } from "../../core/service/PriceQuoteStorage";
import AppointmentOperationSubItemsEdit from "../appointment/AppointmentOperationSubItemsEdit";

const { Text } = Typography;

/**
 * Fiyat Teklifi.
 *
 * Akış (mobil öncelikli, birbirini takip eden basit adımlar):
 *   1) Birim seç → 2) Kurum seç (varsayılan YOK, kullanıcı seçer)
 *   → Randevu türü kurumun varsayılanından otomatik gelir (istenirse değiştirilebilir)
 *   → İşlemleri seç → fiyatları düzenle
 *
 * Bir teklif birden fazla "grup" içerebilir: her grup bir birim + kurum + randevu türü
 * ve o birime ait işlemlerdir. Kullanıcı MR için fiyat gördükten sonra alttan yeni bir
 * birim daha ekleyip aynı akışa devam edebilir.
 */
export default function PriceQuoteModal({ open, onClose, onSaved }) {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    // Master data
    const [units, setUnits] = useState([]);
    const [appointmentTypes, setAppointmentTypes] = useState([]);

    // Teklif içeriği
    // group: { key, unitId, unitName, firmId, firmName, institutionId, appointmentTypeId, operations: [] }
    const [groups, setGroups] = useState([]);
    const [otherOps, setOtherOps] = useState([]); // { id, name, price }
    const [otherForm, setOtherForm] = useState({ name: '', price: 0 });
    const [currency] = useState('TRY');

    // Yeni grup sihirbazı: null = kapalı, { unitId } = birim seçildi kurum bekleniyor
    const [draft, setDraft] = useState(null);
    // Var olan bir grupta kurum / randevu türü değiştirme: { groupKey, field }
    const [editing, setEditing] = useState(null);
    const [institutionSearch, setInstitutionSearch] = useState('');

    // İşlem seçici / alt işlem modalleri
    const [opPickerGroupKey, setOpPickerGroupKey] = useState(null);
    const [opPickerSearch, setOpPickerSearch] = useState('');
    const opPickerSearchRef = useRef(null);
    const keyboardPrimerRef = useRef(null);
    const [subItemsCtx, setSubItemsCtx] = useState(null); // { groupKey, opId }
    const [loading, setLoading] = useState(false);

    // --- CACHE ---
    // Render'ı tetiklemesi gerekmeyen, ama async akış içinde anlık doğru olması gereken
    // veriler ref'te tutulur; ekranı forceRender ile tazeleriz.
    const cacheRef = useRef({ ops: {}, inst: {}, instPrice: {}, atPrice: {} });
    const [, forceRender] = useReducer(x => x + 1, 0);

    const getOperations = (unitId) => cacheRef.current.ops[unitId] || [];
    const getInstitutions = (firmId) => cacheRef.current.inst[firmId] || [];
    const priceKey = (id, unitId) => `${id}_${unitId}`;

    const getInstitutionPrice = (institutionId, unitId, operationId) =>
        (cacheRef.current.instPrice[priceKey(institutionId, unitId)] || [])
            .find(x => x.operationId === operationId)?.price;

    const getAppointmentTypePrice = (appointmentTypeId, unitId, operationId) =>
        (cacheRef.current.atPrice[priceKey(appointmentTypeId, unitId)] || [])
            .find(x => x.operationId === operationId)?.price;

    // --- LOADERS ---
    const loadUnits = async () => {
        const res = await ApiService.Unit.GetUnitList();
        if (res.success) setUnits(res.data);
    };

    const loadAppointmentTypes = async () => {
        const res = await ApiService.Appointment.GetAppointmentTypes();
        if (res.success) setAppointmentTypes(res.data);
    };

    const loadInstitutions = async (firmId) => {
        if (!firmId) return [];
        if (cacheRef.current.inst[firmId]) return cacheRef.current.inst[firmId];
        const res = await ApiService.Institution.GetInstitutionListByFirm(firmId);
        const list = res.success ? res.data : [];
        cacheRef.current.inst[firmId] = list;
        forceRender();
        return list;
    };

    const loadOperations = async (unitId) => {
        if (!unitId) return [];
        if (cacheRef.current.ops[unitId]) return cacheRef.current.ops[unitId];
        const res = await ApiService.Operation.GetOperationList(unitId);
        const list = res.success
            ? res.data.map(x => ({ ...x, subItems: JSON.parse(x.subItems ?? '[]') }))
            : [];
        cacheRef.current.ops[unitId] = list;
        forceRender();
        return list;
    };

    const loadInstitutionPriceList = async (institutionId, unitId, opIds) => {
        const key = priceKey(institutionId, unitId);
        if (!institutionId || !opIds?.length) return;
        if (cacheRef.current.instPrice[key]) return;
        const res = await ApiService.Operation.GetInstitutionPriceList(institutionId, opIds);
        cacheRef.current.instPrice[key] = res.success ? res.data : [];
        forceRender();
    };

    const loadAppointmentTypePriceList = async (appointmentTypeId, unitId, opIds) => {
        const key = priceKey(appointmentTypeId, unitId);
        if (!appointmentTypeId || !opIds?.length) return;
        if (cacheRef.current.atPrice[key]) return;
        const res = await ApiService.Operation.GetAppointmentTypePriceList(appointmentTypeId, opIds);
        cacheRef.current.atPrice[key] = res.success ? res.data : [];
        forceRender();
    };

    // --- INITIAL LOAD ---
    useEffect(() => {
        if (!open) return;
        loadUnits();
        loadAppointmentTypes();
        // Modal açılışında doğrudan birim seçimiyle başla
        setDraft({ unitId: null });
    }, [open]);

    // --- BODY SCROLL LOCK (iOS Safari için: antd'nin default'u yetmiyor) ---
    useEffect(() => {
        if (!open) return;
        const scrollY = window.scrollY;
        const bodyStyle = document.body.style;
        const prev = {
            position: bodyStyle.position,
            top: bodyStyle.top,
            left: bodyStyle.left,
            right: bodyStyle.right,
            width: bodyStyle.width,
            overflow: bodyStyle.overflow,
        };
        bodyStyle.position = 'fixed';
        bodyStyle.top = `-${scrollY}px`;
        bodyStyle.left = '0';
        bodyStyle.right = '0';
        bodyStyle.width = '100%';
        bodyStyle.overflow = 'hidden';
        return () => {
            bodyStyle.position = prev.position;
            bodyStyle.top = prev.top;
            bodyStyle.left = prev.left;
            bodyStyle.right = prev.right;
            bodyStyle.width = prev.width;
            bodyStyle.overflow = prev.overflow;
            window.scrollTo(0, scrollY);
        };
    }, [open]);

    // Birim seçilince kurumları hazırla (kurum listesi kullanıcıya gösterilecek)
    useEffect(() => {
        const run = async () => {
            if (!draft?.unitId) return;
            const unit = units.find(x => x.id === draft.unitId);
            setLoading(true);
            await Promise.all([
                loadInstitutions(unit?.firm?.id),
                loadOperations(draft.unitId),
            ]);
            setLoading(false);
        };
        run();
    }, [draft?.unitId]);

    useEffect(() => {
        if (!opPickerGroupKey) return;
        const timer = setTimeout(() => opPickerSearchRef.current?.focus(), 250);
        return () => clearTimeout(timer);
    }, [opPickerGroupKey]);

    // --- FİYAT HESABI (AppointmentDetailEdit.onSelectOperation ile birebir mantık) ---
    const calculatePrice = (operation, group) => {
        const institutionPrice = getInstitutionPrice(group.institutionId, group.unitId, operation.id);
        const appTypePrice = getAppointmentTypePrice(group.appointmentTypeId, group.unitId, operation.id);
        const appType = appointmentTypes.find(x => x.id === group.appointmentTypeId);

        let price = operation?.price;
        if (appType?.overrideInstitutionPrice && appTypePrice != null) {
            price = appTypePrice;
        } else if (institutionPrice != null) {
            price = institutionPrice;
        }
        return price;
    };

    // --- GRUP İŞLEMLERİ ---
    const updateGroup = (groupKey, updater) => {
        setGroups(gs => gs.map(g => (g.key === groupKey ? { ...g, ...updater(g) } : g)));
    };

    const getGroup = (groupKey) => groups.find(g => g.key === groupKey);

    // Kurum / randevu türü değişince, kullanıcının elle dokunmadığı fiyatları tazele
    const recalcGroupPrices = (group) => ({
        operations: group.operations.map(op => {
            if (op.priceModified || (op.subItems?.length > 0)) return op;
            const operation = getOperations(group.unitId).find(o => o.id === op.operationId) || op.operation;
            const institutionPrice = getInstitutionPrice(group.institutionId, group.unitId, op.operationId);
            return {
                ...op,
                price: calculatePrice(operation, group),
                institutionPrice: institutionPrice ?? operation?.price,
                discount: 0,
            };
        })
    });

    const applyInstitution = async (groupKey, institutionId) => {
        const group = getGroup(groupKey);
        if (!group) return;
        const institution = getInstitutions(group.firmId).find(i => i.id === institutionId);
        const appointmentTypeId = institution?.appointmentType?.id || null;
        const opIds = getOperations(group.unitId).map(x => x.id);

        setLoading(true);
        await loadInstitutionPriceList(institutionId, group.unitId, opIds);
        if (appointmentTypeId) {
            await loadAppointmentTypePriceList(appointmentTypeId, group.unitId, opIds);
        }
        setLoading(false);

        setGroups(gs => gs.map(g => {
            if (g.key !== groupKey) return g;
            const next = { ...g, institutionId, appointmentTypeId };
            return { ...next, ...recalcGroupPrices(next) };
        }));
        setEditing(null);
        setInstitutionSearch('');
    };

    const applyAppointmentType = async (groupKey, appointmentTypeId) => {
        const group = getGroup(groupKey);
        if (!group) return;
        const opIds = getOperations(group.unitId).map(x => x.id);

        setLoading(true);
        await loadAppointmentTypePriceList(appointmentTypeId, group.unitId, opIds);
        setLoading(false);

        setGroups(gs => gs.map(g => {
            if (g.key !== groupKey) return g;
            const next = { ...g, appointmentTypeId };
            return { ...next, ...recalcGroupPrices(next) };
        }));
        setEditing(null);
    };

    // Yeni grup: birim + kurum seçildikten sonra oluşur, ardından işlem seçici açılır
    const createGroup = async (unitId, institutionId) => {
        const unit = units.find(u => u.id === unitId);
        const institution = getInstitutions(unit?.firm?.id).find(i => i.id === institutionId);
        const appointmentTypeId = institution?.appointmentType?.id || null;

        setLoading(true);
        const operations = await loadOperations(unitId);
        const opIds = operations.map(x => x.id);
        await loadInstitutionPriceList(institutionId, unitId, opIds);
        if (appointmentTypeId) {
            await loadAppointmentTypePriceList(appointmentTypeId, unitId, opIds);
        }
        setLoading(false);

        const key = v4();
        setGroups(gs => [...gs, {
            key,
            unitId,
            unitName: unit?.name || '',
            firmId: unit?.firm?.id,
            firmName: unit?.firm?.name || '',
            institutionId,
            appointmentTypeId,
            operations: [],
        }]);
        setDraft(null);
        setInstitutionSearch('');
        // Adımın devamı: işlem seçimi hemen açılsın
        setOpPickerSearch('');
        setOpPickerGroupKey(key);
    };

    const removeGroup = (groupKey) => {
        const group = getGroup(groupKey);
        const doRemove = () => {
            setGroups(gs => gs.filter(g => g.key !== groupKey));
            if (editing?.groupKey === groupKey) setEditing(null);
            if (opPickerGroupKey === groupKey) setOpPickerGroupKey(null);
        };
        if ((group?.operations?.length || 0) === 0) {
            doRemove();
            return;
        }
        Modal.confirm({
            title: 'Birimi çıkar',
            content: `${group.unitName} ve seçili ${group.operations.length} işlem tekliften çıkarılacak. Emin misiniz?`,
            okText: t('Yes'),
            cancelText: t('No'),
            okButtonProps: { danger: true },
            onOk: doRemove,
        });
    };

    // --- İŞLEM SEÇİMİ ---
    const toggleOperation = (groupKey, operationId) => {
        const group = getGroup(groupKey);
        if (!group) return;

        if (group.operations.some(x => x.operationId === operationId)) {
            updateGroup(groupKey, g => ({ operations: g.operations.filter(x => x.operationId !== operationId) }));
            return;
        }

        const operation = getOperations(group.unitId).find(x => x.id === operationId);
        if (!operation) return;
        const institutionPrice = getInstitutionPrice(group.institutionId, group.unitId, operationId);

        updateGroup(groupKey, g => ({
            operations: [...g.operations, {
                ...operation,
                id: v4(),
                operationId: operation.id,
                priceModified: false,
                price: calculatePrice(operation, group),
                institutionPrice: institutionPrice ?? operation?.price,
                discount: 0,
                subItems: [],
                operation,
                processMinute: operation.processMinute,
                accessionNumber: '',
            }]
        }));
    };

    const removeOperation = (groupKey, opId) => {
        updateGroup(groupKey, g => ({ operations: g.operations.filter(x => x.id !== opId) }));
    };

    const updateOperationField = (groupKey, opId, key, value) => {
        updateGroup(groupKey, g => ({
            operations: g.operations.map(op => (op.id === opId ? { ...op, [key]: value } : op))
        }));
    };

    // Fiyat düzenleme. Alt işlem seçilmişse yeni fiyat alt işlemlere oransal dağıtılır
    // (AppointmentDetailEdit ile aynı davranış).
    const updateOperationPrice = (groupKey, opId, newPrice) => {
        setGroups(gs => gs.map(g => {
            if (g.key !== groupKey) return g;
            return {
                ...g,
                operations: g.operations.map(op => {
                    if (op.id !== opId) return op;

                    if (op.subItems?.length > 0) {
                        const subTotal = op.subItems.reduce((sum, item) => sum + (item.price || 0), 0);
                        if (subTotal > 0) {
                            const ratio = newPrice / subTotal;
                            let subItems = op.subItems.map(sub => ({
                                ...sub,
                                price: parseFloat(((sub.price || 0) * ratio).toFixed(2))
                            }));
                            const newSubTotal = subItems.reduce((sum, item) => sum + (item.price || 0), 0);
                            const diff = newPrice - newSubTotal;
                            if (Math.abs(diff) > 0.01) {
                                subItems = subItems.map((sub, i) => (
                                    i === 0 ? { ...sub, price: parseFloat((sub.price + diff).toFixed(2)) } : sub
                                ));
                            }
                            return { ...op, price: newPrice, priceModified: true, subItems };
                        }
                        return { ...op, price: newPrice, priceModified: true };
                    }

                    const institutionPrice = getInstitutionPrice(g.institutionId, g.unitId, op.operationId) ?? op.institutionPrice;
                    const discount = (institutionPrice > newPrice && institutionPrice > 0)
                        ? 100 - (newPrice * 100 / institutionPrice)
                        : 0;
                    return { ...op, price: newPrice, priceModified: true, discount };
                })
            };
        }));
    };

    const saveSubItems = (groupKey, opId, items) => {
        updateGroup(groupKey, g => ({
            operations: g.operations.map(op => (
                op.id === opId
                    ? { ...op, subItems: items, price: items.reduce((sum, x) => sum + (x.price || 0), 0) }
                    : op
            ))
        }));
        setSubItemsCtx(null);
    };

    // --- DİĞER KALEMLER ---
    const addOtherOp = () => {
        if (!otherForm.name || otherForm.price == null) {
            message.warning(t('Other operation name and price are required'));
            return;
        }
        setOtherOps([...otherOps, { id: v4(), name: otherForm.name, price: otherForm.price }]);
        setOtherForm({ name: '', price: 0 });
    };

    const removeOtherOp = (id) => setOtherOps(otherOps.filter(x => x.id !== id));

    // --- TOPLAMLAR ---
    const allOps = groups.flatMap(g => g.operations);
    const opsTotal = allOps.reduce((acc, x) => acc + (x.price || 0), 0);
    const otherTotal = otherOps.reduce((acc, x) => acc + (x.price || 0), 0);
    const grandTotal = opsTotal + otherTotal;
    const itemCount = allOps.length + otherOps.length;
    const groupTotal = (group) => group.operations.reduce((acc, x) => acc + (x.price || 0), 0);

    // --- KAYDET / KAPAT ---
    const handleSave = () => {
        if (itemCount === 0) {
            message.warning(t('Add at least one operation'));
            return;
        }

        const operationList = allOps.map(x => ({
            id: x.id,
            operationId: x.operationId,
            operation: x.operation,
            price: x.price,
            priceModified: x.priceModified,
            institutionPrice: x.institutionPrice,
            discount: x.discount,
            subItems: x.subItems || [],
            processMinute: x.processMinute,
            accessionNumber: x.accessionNumber || '',
        }));

        // Her birim kendi kurumu/randevu türüyle kaydedilir; aktarım sırasında
        // AppointmentDetailEdit ilgili birimin seçimini kullanır.
        const unitSelections = {};
        groups.forEach(g => {
            unitSelections[g.unitId] = {
                unitId: g.unitId,
                unitName: g.unitName,
                firmName: g.firmName,
                institutionId: g.institutionId,
                appointmentTypeId: g.appointmentTypeId,
            };
        });

        const first = groups[0];
        const saved = PriceQuoteStorage.save({
            // Geriye dönük uyum: eski tüketiciler tek birim bekliyor
            unitId: first?.unitId ?? null,
            unitName: first?.unitName ?? '',
            firmName: first?.firmName ?? '',
            institutionId: first?.institutionId ?? null,
            appointmentTypeId: first?.appointmentTypeId ?? null,
            unitSelections,
            currency,
            operationList,
            otherOperations: otherOps.map(x => ({ id: x.id, name: x.name, price: x.price })),
        });

        if (saved) {
            message.success(t('Price quote saved to memory'));
            onSaved && onSaved();
            handleClose();
        } else {
            message.error('Kaydedilemedi');
        }
    };

    const handleClose = () => {
        setGroups([]);
        setOtherOps([]);
        setOtherForm({ name: '', price: 0 });
        setDraft(null);
        setEditing(null);
        setInstitutionSearch('');
        setOpPickerGroupKey(null);
        setOpPickerSearch('');
        setSubItemsCtx(null);
        cacheRef.current = { ops: {}, inst: {}, instPrice: {}, atPrice: {} };
        onClose && onClose();
    };

    const handleReset = () => {
        if (itemCount === 0 && groups.length === 0) return;
        Modal.confirm({
            title: t('Reset the list'),
            content: t('All selected operations and other items will be deleted. Are you sure?'),
            okText: t('Yes'),
            cancelText: t('No'),
            onOk: () => {
                setGroups([]);
                setOtherOps([]);
                setEditing(null);
                setDraft({ unitId: null });
            }
        });
    };

    // --- ORTAK RENDER PARÇALARI ---
    // NOT: Bu yardımcılar bilerek component değil, düz render fonksiyonu.
    // Component olsalardı her render'da yeni tip üretilir, antd Input'ları
    // remount olup arama kutusundaki focus her tuşta kaybolurdu.
    const renderBigButtonGrid = ({ items, onPick, getKey, getLabel, getSubLabel, isDisabled }) => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '180px'}, 1fr))`,
            gap: 8,
            marginBottom: 12,
        }}>
            {items.map(item => {
                const disabled = isDisabled ? isDisabled(item) : false;
                return (
                    <Button
                        key={getKey(item)}
                        size="large"
                        disabled={disabled}
                        onClick={() => onPick(item)}
                        style={{
                            height: 'auto',
                            minHeight: 64,
                            padding: '10px 12px',
                            textAlign: 'left',
                            whiteSpace: 'normal',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            gap: 2,
                            lineHeight: 1.3,
                        }}
                    >
                        {getSubLabel && getSubLabel(item) && (
                            <span style={{ fontSize: 10, color: '#8c8c8c', fontWeight: 400 }}>
                                {getSubLabel(item)}
                            </span>
                        )}
                        <span style={{ fontSize: 14, fontWeight: 500, color: disabled ? '#bfbfbf' : '#262626' }}>
                            {getLabel(item)}
                        </span>
                    </Button>
                );
            })}
        </div>
    );

    const renderStepBanner = ({ num, label, hint }) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            background: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: 6,
            marginBottom: 8,
        }}>
            <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: '#1890ff', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 600, flexShrink: 0
            }}>
                {num}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#262626' }}>{label}</div>
                {hint && <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1 }}>{hint}</div>}
            </div>
        </div>
    );

    const renderInstitutionPicker = ({ firmId, selectedId, onPick }) => {
        const list = getInstitutions(firmId);
        const normalized = Helper.Text.normalizeText(institutionSearch || '');
        const filtered = normalized
            ? list.filter(i => Helper.Text.normalizeText(i.name || '').includes(normalized))
            : list;

        if (list.length === 0) {
            // Henüz yükleniyor olabilir — "kurum yok" demeden önce beklenir
            if (loading) {
                return (
                    <div style={{ textAlign: 'center', padding: '16px 0', marginBottom: 12 }}>
                        <Spin size="small" />
                    </div>
                );
            }
            return <Alert type="info" message="Bu birime ait kurum yok" showIcon style={{ marginBottom: 12 }} />;
        }
        return (
            <>
                {list.length > 6 && (
                    <Input
                        allowClear
                        size="middle"
                        placeholder="Kurum ara..."
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={institutionSearch}
                        onChange={(e) => setInstitutionSearch(e.target.value)}
                        style={{ marginBottom: 8 }}
                    />
                )}
                {filtered.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('No matching institution')}
                        style={{ marginBottom: 12 }}
                    />
                ) : (
                    renderBigButtonGrid({
                        items: filtered,
                        getKey: (i) => i.id,
                        getLabel: (i) => i.name,
                        getSubLabel: (i) => (
                            i.id === selectedId
                                ? '✓ ' + t('Selected')
                                : (i?.appointmentType?.name ? i.appointmentType.name : '')
                        ),
                        onPick: (i) => onPick(i.id),
                    })
                )}
            </>
        );
    };

    // Seçili işlem satırları (mobil: kompakt satır, desktop: tablo)
    const renderOperationList = (group) => {
        if (group.operations.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('No Operations')}
                    style={{ margin: '8px 0' }}
                />
            );
        }

        const subItemsButton = (op) => {
            const available = op?.operation?.subItems || [];
            if (available.length === 0) return null;
            return (
                <Button
                    size="small"
                    icon={<SmallDashOutlined />}
                    onClick={() => setSubItemsCtx({ groupKey: group.key, opId: op.id })}
                    style={{ flexShrink: 0 }}
                >
                    {op.subItems?.length > 0 ? `Alt İşlemler (${op.subItems.length})` : 'Alt İşlemler'}
                </Button>
            );
        };

        if (isMobile) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 8,
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    overflow: 'hidden'
                }}>
                    {group.operations.map((op, idx) => (
                        <div
                            key={op.id}
                            style={{
                                padding: '6px 8px',
                                background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: idx < group.operations.length - 1 ? '1px solid #f5f5f5' : 'none',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: '#262626',
                                    flex: 1,
                                    minWidth: 0,
                                    wordBreak: 'break-word',
                                    lineHeight: 1.3,
                                }}>
                                    {op?.operation?.name}
                                </span>
                                <InputNumber
                                    size="small"
                                    className="pq-compact-price"
                                    prefix={Currency[currency]}
                                    value={op.price}
                                    step={0.01}
                                    min={0}
                                    inputMode="decimal"
                                    pattern="[0-9]*"
                                    onChange={(newPrice) => updateOperationPrice(group.key, op.id, newPrice)}
                                    style={{ width: 110, flexShrink: 0 }}
                                />
                                <Button
                                    size="small"
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeOperation(group.key, op.id)}
                                    style={{ flexShrink: 0 }}
                                />
                            </div>

                            <div style={{
                                fontSize: 10,
                                color: '#bfbfbf',
                                marginTop: 2,
                                display: 'flex',
                                gap: 8,
                                flexWrap: 'wrap',
                                alignItems: 'center'
                            }}>
                                {op.processMinute != null && <span>⏱ {op.processMinute} dk</span>}
                                <span>Liste: {Helper.Currency.format(op?.operation?.price)}</span>
                                {getInstitutionPrice(group.institutionId, group.unitId, op.operationId) != null && (
                                    <span>Kurum: {Helper.Currency.format(getInstitutionPrice(group.institutionId, group.unitId, op.operationId))}</span>
                                )}
                                {getAppointmentTypePrice(group.appointmentTypeId, group.unitId, op.operationId) != null && (
                                    <span>Tür: {Helper.Currency.format(getAppointmentTypePrice(group.appointmentTypeId, group.unitId, op.operationId))}</span>
                                )}
                            </div>

                            {(op?.operation?.subItems?.length > 0) && (
                                <div style={{ marginTop: 6 }}>
                                    {subItemsButton(op)}
                                    {op.subItems?.length > 0 && (
                                        <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 4, lineHeight: 1.4 }}>
                                            {op.subItems.map(s => s.name).join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <Table
                style={{ marginTop: 12 }}
                scroll={{ x: 600 }}
                size="small"
                dataSource={group.operations}
                pagination={false}
                rowKey="id"
                columns={[
                    {
                        key: 'name',
                        dataIndex: 'name',
                        title: t('Name'),
                        render: (_, row) => (
                            <div>
                                <div>{row?.operation?.name}</div>
                                {row?.operation?.subItems?.length > 0 && (
                                    <div style={{ marginTop: 4 }}>
                                        {subItemsButton(row)}
                                        {row.subItems?.length > 0 && (
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                                                {row.subItems.map(s => s.name).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    },
                    {
                        key: 'processMinute',
                        dataIndex: 'processMinute',
                        title: t('Process Minute'),
                        width: 120,
                        render: (_, row) => (
                            <InputNumber
                                style={{ width: '100%' }}
                                size="small"
                                value={row?.processMinute}
                                placeholder={t('Process Minute')}
                                onChange={(val) => updateOperationField(group.key, row.id, 'processMinute', val)}
                            />
                        )
                    },
                    {
                        key: 'price',
                        dataIndex: 'price',
                        width: 240,
                        title: t('Price'),
                        render: (_, row) => (
                            <div>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    size="small"
                                    prefix={Currency[currency]}
                                    value={row.price}
                                    step={0.01}
                                    min={0}
                                    onChange={(newPrice) => updateOperationPrice(group.key, row.id, newPrice)}
                                />
                                <table style={{ fontSize: 11, marginTop: 4 }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ paddingRight: 6, color: '#8c8c8c' }}><b>{t('Price')}:</b></td>
                                            <td>{Helper.Currency.format(row?.operation?.price)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ paddingRight: 6, color: '#8c8c8c' }}><b>{t('Institution')} / {t('Special')}:</b></td>
                                            <td>
                                                {Helper.Currency.format(getInstitutionPrice(group.institutionId, group.unitId, row?.operationId))}
                                                {' / '}
                                                {Helper.Currency.format(getAppointmentTypePrice(group.appointmentTypeId, group.unitId, row?.operationId))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )
                    },
                    {
                        key: '#',
                        title: '#',
                        width: 50,
                        render: (_, row) => (
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeOperation(group.key, row.id)}
                            />
                        )
                    }
                ]}
            />
        );
    };

    // --- GRUP KARTI ---
    const renderGroupCard = (group, index) => {
        const institution = getInstitutions(group.firmId).find(i => i.id === group.institutionId);
        const appointmentType = appointmentTypes.find(at => at.id === group.appointmentTypeId);
        const isEditingInstitution = editing?.groupKey === group.key && editing?.field === 'institution';
        const isEditingType = editing?.groupKey === group.key && editing?.field === 'appointmentType';

        return (
            <div
                key={group.key}
                style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    marginBottom: 12,
                    overflow: 'hidden',
                    background: '#fff',
                }}
            >
                {/* Başlık: birim */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    background: '#f6ffed',
                    borderBottom: '1px solid #f0f0f0',
                }}>
                    <div style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: '#52c41a', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 600, flexShrink: 0
                    }}>
                        {index + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: '#8c8c8c', lineHeight: 1 }}>
                            {group.firmName || 'Birim'}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginTop: 2, wordBreak: 'break-word' }}>
                            {group.unitName}
                        </div>
                    </div>
                    {group.operations.length > 0 && (
                        <Tag color="blue" style={{ margin: 0 }}>
                            {Helper.Currency.format(groupTotal(group), { currency })}
                        </Tag>
                    )}
                    <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeGroup(group.key)}
                    />
                </div>

                {/* Kurum + randevu türü özeti */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    padding: '6px 10px',
                    borderBottom: '1px solid #f5f5f5',
                    fontSize: 12,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                        <span style={{ color: '#8c8c8c' }}>Kurum:</span>
                        <span style={{ fontWeight: 500, wordBreak: 'break-word' }}>
                            {institution?.name || '—'}
                        </span>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: '0 4px', height: 'auto', fontSize: 11 }}
                            onClick={() => {
                                setInstitutionSearch('');
                                setEditing(isEditingInstitution ? null : { groupKey: group.key, field: 'institution' });
                            }}
                        >
                            {isEditingInstitution ? t('Close') : t('Change')}
                        </Button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                        <span style={{ color: '#8c8c8c' }}>{t('Appointment Type')}:</span>
                        <span style={{ fontWeight: 500, wordBreak: 'break-word' }}>
                            {appointmentType?.name || '—'}
                        </span>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: '0 4px', height: 'auto', fontSize: 11 }}
                            onClick={() => setEditing(isEditingType ? null : { groupKey: group.key, field: 'appointmentType' })}
                        >
                            {isEditingType ? t('Close') : t('Change')}
                        </Button>
                    </div>
                </div>

                {/* Kurum değiştirme paneli */}
                {isEditingInstitution && (
                    <div style={{ padding: '8px 10px 0 10px', background: '#fafafa' }}>
                        {renderInstitutionPicker({
                            firmId: group.firmId,
                            selectedId: group.institutionId,
                            onPick: (id) => applyInstitution(group.key, id),
                        })}
                    </div>
                )}

                {/* Randevu türü değiştirme paneli */}
                {isEditingType && (
                    <div style={{ padding: '8px 10px 0 10px', background: '#fafafa' }}>
                        {appointmentTypes.length === 0 ? (
                            <Alert type="info" message={t('No appointment type')} showIcon style={{ marginBottom: 12 }} />
                        ) : (
                            renderBigButtonGrid({
                                items: appointmentTypes,
                                getKey: (at) => at.id,
                                getLabel: (at) => at.name,
                                getSubLabel: (at) => (at.id === group.appointmentTypeId ? '✓ ' + t('Selected') : ''),
                                onPick: (at) => applyAppointmentType(group.key, at.id),
                            })
                        )}
                    </div>
                )}

                {/* İşlemler */}
                <div style={{ padding: '8px 10px 10px 10px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                    }}>
                        <Text strong style={{ fontSize: 13 }}>
                            İşlemler {group.operations.length > 0 && `(${group.operations.length})`}
                        </Text>
                        <Button
                            size="middle"
                            type={group.operations.length === 0 ? 'primary' : 'default'}
                            icon={<PlusOutlined />}
                            onClick={() => {
                                try { keyboardPrimerRef.current?.focus({ preventScroll: true }); } catch { /* ignore */ }
                                setOpPickerSearch('');
                                setOpPickerGroupKey(group.key);
                            }}
                        >
                            {t('Select Operation')}
                        </Button>
                    </div>
                    {renderOperationList(group)}
                </div>
            </div>
        );
    };

    // --- YENİ BİRİM SİHİRBAZI ---
    const renderDraft = () => {
        if (!draft) return null;
        const usedUnitIds = groups.map(g => g.unitId);

        if (!draft.unitId) {
            return (
                <div style={{
                    border: '1px dashed #91d5ff',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 12,
                    background: '#fafcff',
                }}>
                    {renderStepBanner({
                        num: 1,
                        label: 'Birim seçin',
                        hint: groups.length > 0 ? 'Teklife eklemek istediğiniz yeni birim' : 'Fiyat almak istediğiniz birim',
                    })}
                    {renderBigButtonGrid({
                        items: units,
                        getKey: (u) => u.id,
                        getLabel: (u) => u.name,
                        getSubLabel: (u) => (
                            usedUnitIds.includes(u.id) ? '✓ Eklendi' : (u?.firm?.name || '')
                        ),
                        isDisabled: (u) => usedUnitIds.includes(u.id),
                        onPick: (u) => setDraft({ unitId: u.id }),
                    })}
                    {groups.length > 0 && (
                        <Button block onClick={() => setDraft(null)}>{t('Cancel')}</Button>
                    )}
                </div>
            );
        }

        const unit = units.find(u => u.id === draft.unitId);
        return (
            <div style={{
                border: '1px dashed #91d5ff',
                borderRadius: 8,
                padding: 10,
                marginBottom: 12,
                background: '#fafcff',
            }}>
                {renderStepBanner({
                    num: 2,
                    label: 'Kurum seçin',
                    hint: `${unit?.firm?.name ? unit.firm.name + ' — ' : ''}${unit?.name || ''} · randevu türü kuruma göre otomatik gelir`,
                })}
                {renderInstitutionPicker({
                    firmId: unit?.firm?.id,
                    selectedId: null,
                    onPick: (institutionId) => createGroup(draft.unitId, institutionId),
                })}
                <Button block onClick={() => setDraft({ unitId: null })}>Birimi değiştir</Button>
            </div>
        );
    };

    // --- İŞLEM SEÇİCİ MODAL ---
    const pickerGroup = getGroup(opPickerGroupKey);
    const pickerOperations = pickerGroup
        ? getOperations(pickerGroup.unitId).filter(x =>
            !opPickerSearch || Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(opPickerSearch)))
        : [];

    // --- ALT İŞLEM MODAL ---
    const subItemsGroup = subItemsCtx ? getGroup(subItemsCtx.groupKey) : null;
    const subItemsOp = subItemsGroup?.operations.find(x => x.id === subItemsCtx?.opId);

    return (
        <>
            <style>{`
                .pq-compact-price .ant-input-number-input { padding: 0 4px !important; }
                .pq-compact-price .ant-input-number-prefix { margin-right: 2px !important; margin-left: 4px !important; font-size: 11px; opacity: 0.7; }
            `}</style>
            <Modal
                title={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 24 }}>
                        <span>{t('Create Price Quote')}</span>
                        {itemCount > 0 && (
                            <Tag color="blue" style={{ margin: 0 }}>{itemCount} kalem</Tag>
                        )}
                    </div>
                )}
                open={open}
                onCancel={handleClose}
                width={900}
                centered
                style={{
                    maxWidth: 'calc(100vw - 16px)',
                    top: isMobile ? 8 : undefined,
                    paddingBottom: 0,
                }}
                bodyStyle={{
                    // 100dvh: mobil tarayıcı bar'ları hesaba katılır (100vh iOS'ta yanlış)
                    maxHeight: isMobile ? 'calc(100dvh - 220px)' : 'calc(100vh - 220px)',
                    overflowY: 'auto',
                    padding: isMobile ? 12 : 16,
                    WebkitOverflowScrolling: 'touch',
                }}
                destroyOnClose
                footer={(
                    <div>
                        {itemCount > 0 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 10px',
                                background: '#e6f7ff',
                                borderRadius: 4,
                                marginBottom: 8,
                                fontSize: 13
                            }}>
                                <span style={{ color: '#595959' }}>
                                    Toplam {itemCount} kalem
                                    {groups.length > 1 && ` · ${groups.length} birim`}
                                </span>
                                <span style={{ fontWeight: 700, fontSize: 16, color: '#096dd9' }}>
                                    {Helper.Currency.format(grandTotal, { currency })}
                                </span>
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Button icon={<ClearOutlined />} onClick={handleReset} danger>
                                Sıfırla
                            </Button>
                            <Button onClick={handleClose}>{t('Cancel')}</Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                disabled={itemCount === 0}
                            >
                                Hafızaya Kaydet
                            </Button>
                        </div>
                    </div>
                )}
            >
                {/* iOS mobil klavye primer — picker açılırken user-gesture ile focus */}
                <input
                    ref={keyboardPrimerRef}
                    type="text"
                    inputMode="search"
                    aria-hidden="true"
                    tabIndex={-1}
                    readOnly
                    style={{
                        position: 'fixed', top: 0, left: 0, width: 1, height: 1,
                        opacity: 0, pointerEvents: 'none', border: 'none',
                        background: 'transparent', zIndex: -1,
                    }}
                />

                {loading && (
                    <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <Spin size="small" />
                    </div>
                )}

                {/* Eklenmiş birim grupları */}
                {groups.map((group, index) => renderGroupCard(group, index))}

                {/* Yeni birim sihirbazı */}
                {renderDraft()}

                {/* Başka birim ekle — eklenecek birim kalmadıysa gizlenir */}
                {!draft && groups.length < units.length && (
                    <Button
                        block
                        type="dashed"
                        size="large"
                        icon={<ApartmentOutlined />}
                        onClick={() => {
                            setEditing(null);
                            setInstitutionSearch('');
                            setDraft({ unitId: null });
                        }}
                        style={{ marginBottom: 12 }}
                    >
                        Başka birim ekle
                    </Button>
                )}

                {/* --- DİĞER KALEMLER --- */}
                <Divider style={{ margin: '16px 0 12px 0' }} />
                <Text strong>Diğer Kalemler ({otherOps.length})</Text>
                <div style={{ display: 'flex', gap: 6, marginTop: 8, marginBottom: 8 }}>
                    <Input
                        placeholder="Ad"
                        value={otherForm.name}
                        onChange={(e) => setOtherForm({ ...otherForm, name: e.target.value })}
                        size="middle"
                        style={{ flex: 1, minWidth: 0 }}
                        onPressEnter={addOtherOp}
                    />
                    <InputNumber
                        placeholder="Fiyat"
                        value={otherForm.price}
                        onChange={(val) => setOtherForm({ ...otherForm, price: val })}
                        size="middle"
                        min={0}
                        step={0.01}
                        prefix={Currency[currency]}
                        style={{ width: 130 }}
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={addOtherOp} size="middle" />
                </div>
                {otherOps.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                        {otherOps.map(o => (
                            <div
                                key={o.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '6px 10px',
                                    background: '#fafafa',
                                    borderRadius: 4
                                }}
                            >
                                <span style={{ flex: 1, minWidth: 0 }}>{o.name}</span>
                                <span style={{ fontWeight: 600, marginRight: 8 }}>
                                    {Helper.Currency.format(o.price, { currency })}
                                </span>
                                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeOtherOp(o.id)} />
                            </div>
                        ))}
                    </div>
                )}
            </Modal>

            {/* İşlem seçici */}
            <Modal
                title={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 24 }}>
                        <span>{t('Select Operation')}</span>
                        {pickerGroup && (
                            <Tag color="blue" style={{ margin: 0 }}>
                                {pickerGroup.unitName} · {pickerGroup.operations.length} seçili
                            </Tag>
                        )}
                    </div>
                )}
                open={!!opPickerGroupKey}
                onCancel={() => setOpPickerGroupKey(null)}
                footer={null}
                width={isMobile ? '100%' : 640}
                centered={!isMobile}
                style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, padding: 0 } : undefined}
                bodyStyle={{
                    padding: 12,
                    height: isMobile ? 'calc(100vh - 110px)' : 'min(70vh, 560px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                destroyOnClose
            >
                <Input
                    ref={opPickerSearchRef}
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder={t('Search')}
                    size="middle"
                    value={opPickerSearch}
                    onChange={(e) => setOpPickerSearch(e.target.value)}
                    allowClear
                    style={{ marginBottom: 8 }}
                />
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                    marginBottom: 8
                }}>
                    {pickerOperations.map(item => {
                        const selected = pickerGroup.operations.some(x => x.operationId === item.id);
                        return (
                            <div
                                key={item.id}
                                onClick={() => toggleOperation(pickerGroup.key, item.id)}
                                style={{
                                    padding: '12px 10px',
                                    borderBottom: '1px solid #f5f5f5',
                                    backgroundColor: selected ? '#e6f7ff' : 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    cursor: 'pointer'
                                }}
                            >
                                <div style={{
                                    width: 22, height: 22,
                                    border: `2px solid ${selected ? '#1890ff' : '#d9d9d9'}`,
                                    borderRadius: 4,
                                    background: selected ? '#1890ff' : 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white', fontSize: 14, lineHeight: 1, flexShrink: 0
                                }}>
                                    {selected && '✓'}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: selected ? 600 : 400, fontSize: 14, wordBreak: 'break-word' }}>
                                        {item.name}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#999', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <span>{item.processMinute} {t('minute')}</span>
                                        {item.subItems?.length > 0 && (
                                            <span style={{ color: '#1890ff' }}>
                                                {item.subItems.length} alt işlem
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {pickerOperations.length === 0 && (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t('No Data')}
                            style={{ padding: 24 }}
                        />
                    )}
                </div>
                <Button
                    type="primary"
                    block
                    size="large"
                    onClick={() => setOpPickerGroupKey(null)}
                >
                    Tamam ({pickerGroup?.operations.length || 0})
                </Button>
            </Modal>

            {/* Alt işlemler */}
            <Modal
                title={`Alt İşlemler${subItemsOp ? ' — ' + (subItemsOp?.operation?.name || '') : ''}`}
                open={!!subItemsCtx}
                onCancel={() => setSubItemsCtx(null)}
                footer={null}
                width={isMobile ? '100%' : 760}
                centered={!isMobile}
                style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, padding: 0 } : undefined}
                bodyStyle={{
                    padding: 12,
                    maxHeight: isMobile ? 'calc(100dvh - 120px)' : 'calc(100vh - 200px)',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                }}
                destroyOnClose
            >
                {subItemsOp && (
                    <AppointmentOperationSubItemsEdit
                        currency={currency}
                        subItems={subItemsOp?.operation?.subItems || []}
                        selectedSubItems={subItemsOp.subItems ?? []}
                        onSave={(items) => saveSubItems(subItemsCtx.groupKey, subItemsCtx.opId, items)}
                    />
                )}
            </Modal>
        </>
    );
}
